import React from 'react'
import { Link } from 'react-router-dom'
import { Tooltip } from '@material-ui/core'
import FlagIcon from 'components/shared/FlagIcon'
import moment from 'moment'
import Moment from "react-moment"
import { capitalize, startCase } from 'lodash';
import strings from 'strings'
import { Status } from './chartData'
import PrepareTooltip from 'components/shared/Tooltip'
import { Icon } from 'styles';
import { changeTypeLabels, businessTypeOption } from './constantVariables';
import { formatTooltip, joinAttributes, translateRisk } from 'tools/formatters'
import { isScreenSmall } from 'tools/utils'
import {Risk} from 'constants/chartData'
import {OnboardedToFC} from 'constants/chartData'

const EMPTY_FIELD = '---'

const emptyFormatter = (cell) => {
    return (
        cell ? cell : EMPTY_FIELD
    )
}

// Columns without their own formatter show "---" for empty values too, like the ones that have one
const withEmptyFormatter = (columns) => columns.map(column =>
    column.formatter || column.dataField === 'action' ? column : { ...column, formatter: emptyFormatter })

const numberFormatter = (cell) => {
    return cell ? cell : 0
}

const upperCaseFormatter = (cell) => {
    return (
        cell ? cell.toUpperCase() : EMPTY_FIELD
    )
}


const amountsFormatter = (amount) => {
    const style = amount < 0 ? 'negative-amount' : 'positive-amount'
    return <div className={style}>$ {amount}</div>
}

export const fcsDatesFormatter = (date) => {
    if (!date) {
        return '-'
    }
    return moment.utc(date).format('MM/DD/YYYY')
}

export const uccFilingDebtorNameFormatter = (cell, row) => {
    return <>
                {row.entityType === 'BUSINESS_DBA' &&
                    <Icon className={'dbaLogo'} folder='icons' fileName='DBA.svg'/>}
                <p title={cell}>{cell}</p>
            </>
}

export const uccStatusLabel = (cell) => {
    return <span className={`${cell?.labelColor}`}>
        {cell.status}
    </span>
}

// TODO need to standardize ALL the formaters into the amount of cases needed, why do we have some dates with MMM and others with MM/DD?
// Used for dates that comes as 2024-01-01T00:00:00Z and then the browser applies the timezone and displays the date wrong
const momentOnlyDateFormatter = (date) => {
    return date ? <Moment format="MM/DD/YYYY" utc>{date}</Moment> : emptyFormatter()
}
const momentDateFormatter = (date) => {
    return date ? <Moment format="MM/DD/YYYY">{date}</Moment> : emptyFormatter()
}
const dateFormatter = (date) => {
    return date ? <Moment format="Do MMM YYYY">{date}</Moment> : emptyFormatter()
}

function fileTypeFormatter(fileType) {
    const fileTypeMap = {
        'application/pdf': 'PDF',
        'image/jpeg': 'JPEG Image',
        'image/png': 'PNG Image',
        'image/gif': 'GIF Image',
        'text/plain': 'Plain Text',
        'text/html': 'HTML Document',
        'application/msword': 'MS Word Document',
        'application/vnd.ms-excel': 'MS Excel Spreadsheet',
        'application/vnd.ms-powerpoint': 'MS PowerPoint Presentation',
        'application/zip': 'ZIP Archive',
        'application/x-rar-compressed': 'RAR Archive',
    };
    if (fileType in fileTypeMap) {
        return fileTypeMap[fileType];
    } else {
        return fileType;
    }
}

const statusFormatter = (cell, row) => {
    const status = row.status === 'Hit & Completed' || row.status === 'In-Progress' ? {
        name: row.status === 'In-Progress' ? 'In-Progress' : 'Completed',
        className: 'completed'
    } : {
        name: 'Not Started',
        className: 'notStarted'
    }
    return (
        <div className={`${status.className} tableStatus`}>{status.name}</div>
    )
}

const emptyColumnFormatter = (cell) => {
    return cell ? cell : '-';
}


const FmcsaColumns = [
    'Business Name',
    'DBA Name',
    'Address',
    'EIN',
    'DOT',
    'Status'
]

const getValue = key => key ? key : '-'

const getValueFromData = (data, field) => {
    if (data !== null && data !== undefined) {
        if (field === 'categories' && Array.isArray(data[field])) return data[field].join(', ')
        if (data[field] != null && data[field] !== undefined){
            return data[field]
        }else{
            return '-'
        }
    }
    return '-';
}

export function mapCompanyInformation(data) {
    return [
        {
            title: 'Name :',
            value: getValue(data?.basicInformation?.businessName)
        },
        {
            title: 'Address :',
            value: getValue(data?.basicInformation?.contactAddress.simpleValue)
        },
        {
            title: 'Country :',
            value: getValue(data?.basicInformation?.country)
        },
        {
            title: 'Status :',
            value: getValue(data?.basicInformation?.companyStatus?.status)
        },
        {
            title: 'Registration Date :',
            value: moment(getValue(data?.basicInformation?.companyRegistrationDate)).format("MMM Do YY")
        },
        {
            title: 'Classification :',
            value: getValue(data?.mainActivity?.classification)
        },
        {
            title: 'Description :',
            value: getValue(data?.mainActivity?.description)
        },
        {
            title: 'Industry Sector :',
            value: getValue(data?.mainActivity?.industrySector)
        },
        {
            title: 'Code :',
            value: getValue(data?.mainActivity?.code)
        },
    ]
}

export function mapCurrentCreditScore(data) {
    return [
        {
            title: 'Current Credit Limit :',
            value: `${getValue(data?.currentCreditRating?.creditLimit?.value)} ${getValue(data?.currentCreditRating?.creditLimit?.currency)}`
        },
        {
            title: 'Credit Value :',
            value: getValue(data?.currentCreditRating?.providerValue?.value)
        },
        {
            title: 'Latest Rating Change :',
            value: moment(getValue(data?.latestRatingChangeDate)).format("MMM Do YY")
        },
        {
            title: 'Risk :',
            value: getValue(data?.currentCreditRating?.commonDescription)
        },
    ]
}

export function mapPreviousCreditScore(data) {
    return [
        {
            title: 'Current Credit Limit :',
            value: `${getValue(data?.previousCreditRating?.creditLimit?.value)} ${getValue(data?.previousCreditRating?.creditLimit?.currency)}`
        },
        {
            title: 'Credit Value :',
            value: getValue(data?.previousCreditRating?.providerValue?.value)
        },
        {
            title: 'Latest Rating Change :',
            value: moment(getValue(data?.latestRatingChangeDate)).format("MMM Do YY")
        },
        {
            title: 'Risk :',
            value: getValue(data?.previousCreditRating?.commonDescription)
        },
    ]
}

function mapDataToTable(props, showOnlyBranch) {
    const {
        negativeInformation,
        additionalInformation,
        paymentData,
        extendedPaymentData,
    } = props

    const getData = (data) => {
        if (!data)
            return []
        if (typeof data === 'object' && !Object.entries(data).length)
            return []
        return data
    }

    if (showOnlyBranch)
        return [
            {
                title: strings.APPLICANT.CREDIT_CHECK.BRANCHES,
                columns: BranchOfficesColumn,
                data: additionalInformation?.branchOffices || []
            }
        ]

    return [
        {
            title: strings.APPLICANT.CREDIT_CHECK.PAYMENT_SUMMARY,
            columns: TradePaymentSummaryColumns,
            data: [paymentData?.tradePaymentSummary || {}]
        },
        {
            title: strings.APPLICANT.CREDIT_CHECK.FILING_SUMMARY,
            columns: FilingSummaryColumns,
            data: getData(negativeInformation)
        },
        {
            title: strings.APPLICANT.CREDIT_CHECK.TRADE_LINES_INFORMATION,
            columns: TradeLinesInformationColumn,
            data: [extendedPaymentData?.tpdTradeLinesInformation || {}]
        },
        {
            title: strings.APPLICANT.CREDIT_CHECK.TRADE_PAYMENTS_BY_CREDIT,
            columns: tradePaymentsByCreditExtended,
            data: getData(extendedPaymentData?.tradePaymentsByCreditExtended)
        },
        {
            title: strings.APPLICANT.CREDIT_CHECK.LIFE_TIME_HIGH_BALANCE,
            columns: LifetimeHighBalanceChartColumns,
            data: getData(extendedPaymentData?.lifetimeHighBalanceChart)
        },
        {
            title: strings.APPLICANT.CREDIT_CHECK.RAW_DATA
        }
    ]
}


const ApplicantColumns = [
    {
        dataField: 'applicantName',
        text: 'Business Name',
        sort: true,
        headerStyle: {
            width: '18rem'
        },
        formatter: (cell, row) => {
            return (
                // Flex so a long name is the part that truncates, and the flag stays visible
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Link className={'id-hyper-link'}
                        to={`/applicant/${row.uuid}`}
                        title={cell}
                        style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                        {cell}
                    </Link>
                    {row.businessTypeFlagged &&
                        <FlagIcon title={`${row.businessType} is a flagged business type in your settings`}
                            style={{ marginLeft: 4 }} />}
                </div>
            )
        }
    },
    {
        dataField: 'ownerName',
        text: 'Owner Name',
        sort: true,
        formatter: emptyFormatter,
        headerStyle: {
            width: '10rem'
        },
    },
    {
        dataField: 'creatorName',
        text: 'Creator',
        sort: false,
        formatter: emptyFormatter,
        headerStyle: {
            width: '9rem'
        },
    },
    {
        dataField: 'salesRepName',
        text: 'Sales Rep',
        sort: false,
        formatter: emptyFormatter,
        headerStyle: {
            width: '9rem'
        },
    },
    {
        dataField: 'debtorApplication',
        text: 'Type',
        sort: true,
        formatter: (cell) => cell ? 'Debtor' : 'Client',
        headerStyle: {
            width: '9rem'
        },
    },
    {
        dataField: 'status',
        text: 'Status',
        sort: true,
        headerStyle: {
            textAlign: 'center',
            width: '13rem'
        },
        checkboxDisable: true,
        formatter: (cell) => {
            const status = Status.find(st => st.status === cell)
            return <div className='table-label' style={{ background: status?.code, color: status?.color, borderColor: status?.border }}>{status?.abbreviation}</div>
        }
    },
    {
        dataField: 'factorCloudStatus',
        text:  'FactorCloud Status',
        sort: false,
        headerStyle: {
            textAlign: 'center',
            // Cells clip overflow, so the column must fit the longest chip ("ONBOARDING DISABLED") plus cell padding.
            width: '15rem'
        },
        checkboxDisable: true,
        formatter: (cell) => {
            const status = OnboardedToFC.find(st => st.status === cell)
            return <div className='table-label' style={{ background: status?.code, color: status?.color, borderColor: status?.border }}>{status?.abbreviation}</div>
        }
    },
    {
        dataField: 'createdAt',
        text: 'Created',
        sort: true,
        formatter: momentDateFormatter,
        headerStyle: {
            width: '8rem'
        },
    },
    {
        dataField: 'updatedAt',
        text: 'Last Updated',
        sort: true,
        formatter: momentDateFormatter,
        headerStyle: {
            width: '9rem'
        },
    },
    {
        dataField: 'action',
        headerStyle: {
            width: '7rem',
            cursor: 'pointer'
        },
        sort: false,
        text: ''
    }
]

const UccSearchColums = [
    {
        dataField: 'name',
        text: 'Name',
        headerStyle: {
            width: '15rem'
        },
    },
    {
        dataField: 'state',
        text: 'State',
        formatter: emptyFormatter,
        headerStyle: {
            width: '4rem'
        },
    },
    {
        dataField: 'date',
        text: 'Date',
        formatter: momentDateFormatter,
        headerStyle: {
            width: '8rem'
        },
    },
    {
        dataField: 'status',
        text: 'Status',
        headerStyle: {
            width: '10rem'
        },
        formatter: statusFormatter
    },
    {
        dataField: 'numberOfFilingsSaved',
        text: 'Number of filings',
        headerStyle: {
            width: '5rem',
        },
        formatter: (cell) => cell >= 0 ? <div className='noOfHits'>{cell}</div> : emptyFormatter()
    },
    {
        dataField: 'action',
        headerStyle: {
            width: '7rem',
            cursor: 'pointer'
        },
        text: ''
    }
]

const uccSearchTableColumns = [
    {
        dataField: 'name',
        text: 'Name',
        headerStyle: {
            width: isScreenSmall ? '30%' : '25%'
        },
    },
    {
        dataField: 'state',
        text: 'State',
        formatter: emptyFormatter,
        headerStyle: {
            width: '10%'
        },
    },
    {
        dataField: 'date',
        text: 'Search Date',
        formatter: momentDateFormatter,
        headerStyle: {
            width: '15%'
        },
        hidden: isScreenSmall()
    },
    {
        dataField: 'lastSearchDate',
        text: 'Last Search Date',
        formatter: momentDateFormatter,
        headerStyle: {
            width: '15%'
        },
        hidden: isScreenSmall()
    },
    {
        dataField: 'numberOfFilingsSaved',
        text: 'Filings Attached',
        formatter: numberFormatter,
        headerStyle: {
            width: isScreenSmall ? '20%' : '15%'
        },
    },
    {
        dataField: 'statusLabel',
        text: 'Status',
        formatter: uccStatusLabel,
        headerStyle: {
            width: isScreenSmall ? '25%' : '20%'
        },
    },
    {
        dataField: 'action',
        text: 'Action',
        headerStyle: {
            width: '15%'
        },
    }
]

const uccFilingsTableColumns = [
    {
        dataField: 'debtorName',
        text: 'Name',
        formatter: uccFilingDebtorNameFormatter,
        headerStyle: {
            width: isScreenSmall ? '25%' :'20%'
        },
        style: { display: 'flex' }
    },
    {
        dataField: 'state',
        formatter: emptyColumnFormatter,
        text: 'State',
        headerStyle: {
            width: isScreenSmall ? '10%' : '5%'
        },
    },
    {
        dataField: 'address',
        formatter: emptyColumnFormatter,
        text: 'Address',
        headerStyle: {
            width: '15%'
        },
        hidden: isScreenSmall()
    },
    {
        dataField: 'filingNumber',
        formatter: emptyColumnFormatter,
        text: 'Filing Number',
        headerStyle: {
            width: isScreenSmall ? '20%' : '15%'
        }
    },
    {
        dataField: 'creationDate',
        text: 'Submission Date',
        formatter: fcsDatesFormatter,
        headerStyle: {
            width: isScreenSmall ? '20%' : '15%'
        }
    },
    {
        dataField: 'lapseDate',
        text: 'Lapse Date',
        formatter: fcsDatesFormatter,
        headerStyle: {
            width: '15%'
        },
        hidden: isScreenSmall()
    },
    {
        dataField: 'statusLabel',
        text: 'Status',
        formatter: uccStatusLabel,
        headerStyle: {
            width: isScreenSmall ? '20%' : '15%'
        },
    },
    {
        dataField: 'action',
        headerStyle: {
            display: 'hidden',
            width: '4rem'
        },
        style: { textAlign: 'center' },
        text: ''
    }
]

const auditColums = [
    {
        dataField: 'user',
        text: 'User',
    },
    {
        dataField: 'action',
        text: 'Action',
    },
    {
        dataField: 'date',
        text: 'Date',
        formatter: fcsDatesFormatter
    },
    {
        dataField: 'status',
        text: 'Status',
        formatter: (cell, row) => {
            let statusClassName = ''
            //TODO: Add more cases
            switch (cell) {
                case 'Success':
                    statusClassName = 'greenLabel'
                    break;
                default:
                    statusClassName = 'redLabel'
                    break;
            }
            return (
                <div className={statusClassName}>{row?.message ? row.message : EMPTY_FIELD}</div>
            )
        }
    }
]

const uccSearchResultColumns = [
    {
        dataField: 'FilingNumber',
        text: 'Filing Number'
    },
    {
        dataField: 'FilingType',
        text: 'Filing Type'
    },
    {
        dataField: 'State',
        text: 'State',
    }
]

const uccStatusformatter = {
    formatter: (cell, row) => {
        let status = {
            name: row.status,
            className: 'notStarted'
        }
        if (row.status) {
            if (row.status === 'Recorded')
                status = {
                    name: row.status,
                    className: 'completed'
                }
            else if (row.status === 'Unsuccessful' || row.status === 'Preparation')
                status = {
                    name: row.status,
                    className: 'unSuccess'
                }
            else if (row.status === 'Pending')
                status = {
                    name: row.status,
                    className: 'pending'
                }

            return (
                <div className={`${status.className} tableStatus`}>{status.name}</div>
            )
        }
        return emptyFormatter()
    }
}

const UccFilingColums = [
    {
        dataField: 'debtorName',
        text: 'Name',
        headerStyle: {
            minWidth: '10rem'
        },
        style: { display: 'flex' },
        formatter: (cell, row) => {
            return <>
                {
                    row.entityType === 'BUSINESS_DBA' &&
                    <PrepareTooltip
                        theme='white'
                        title='Doing Business As'
                        placement='top'
                    >
                        <Icon folder='icons' fileName='DBA.svg' className='mr-2' />
                    </PrepareTooltip>
                }
                <div style={row.entityType === 'BUSINESS_DBA' ? { marginTop: '1.5px' } : {}} title={cell}>{cell}</div>
            </>
        }
    },
    {
        dataField: 'address',
        text: 'Address',
        formatter: formatTooltip
    },
    {
        dataField: 'state',
        text: 'State',
        headerStyle: {
            width: '4rem'
        },
        formatter: emptyFormatter
    },
    {
        dataField: 'filingNumber',
        text: 'Filing Number',
        formatter: emptyFormatter,
        hidden: isScreenSmall()
    },
    {
        dataField: 'creationDate',
        text: 'Submission Date',
        formatter: momentOnlyDateFormatter,
        hidden: isScreenSmall()
    },
    {
        dataField: 'lapseDate',
        text: 'Lapse Date',
        formatter: momentOnlyDateFormatter,
        hidden: isScreenSmall()
    },
    {
        dataField: 'status',
        text: 'Status',
        headerStyle: {
            width: '10rem'
        },
        formatter: uccStatusformatter.formatter
    },
    {
        dataField: 'action',
        headerStyle: {
            display: 'hidden',
            width: '4rem'
        },
        style: { textAlign: 'center' },
        text: ''
    }
]

const DirectAmendmentColums = [
    {
        dataField: 'debtorName',
        text: 'Name',
        headerStyle: {
            minWidth: '15rem'
        },
    },
    {
        dataField: 'state',
        text: 'State',
        formatter: emptyFormatter
    },
    {
        dataField: 'jurisdiction',
        text: 'Jurisdiction',
        formatter: emptyFormatter
    },
    {
        dataField: 'creationDate',
        text: 'Created Date',
        formatter: momentOnlyDateFormatter
    },
    {
        dataField: 'status',
        text: 'Status',
        headerStyle: {
            width: '11rem'
        },
        formatter: uccStatusformatter.formatter
    },
    {
        dataField: 'action',
        headerStyle: { display: 'hidden' },
        style: { textAlign: 'center' },
        text: ''
    }
]

const AmendmentHistoryColumns = [
    {
        dataField: 'fileNumber',
        text: 'Filing Number',
        formatter: emptyFormatter
    },
    {
        dataField: 'amendmentAction',
        text: 'Amendment Action',
        formatter: (cell, { amendmentType }) => {
            if (cell && amendmentType) {
                const value = changeTypeLabels[cell.toLowerCase().includes('no') ? amendmentType : cell]
                return <div title={value}>{value}</div>
            }
            return emptyFormatter
        }
    },
    {
        dataField: 'fileDate',
        text: 'Filing Date',
        formatter: emptyFormatter
    },
    {
        dataField: 'uccFilingStatus',
        text: 'Filing Status',
        formatter: emptyFormatter
    },
    {
        dataField: 'action',
        text: '',
        headerStyle: { display: 'hidden' },
        style: { textAlign: 'center' }
    }
]

const FilingImageColumns = [
    {
        dataField: 'createdAt',
        text: 'Requested At',
        formatter: (cell) => {
            return dateFormatter(cell)
        },
        headerStyle: {
            width: '11rem'
        },
    },
    {
        dataField: 'fileType',
        text: 'File Type',
        formatter: (cell) => {
            return fileTypeFormatter(cell)
        },
        headerStyle: {
            width: '8rem'
        },
    },
    {
        dataField: 'purpose',
        text: 'Description',
        headerStyle: {
            width: '20rem'
        },
    },
    {
        dataField: 'action',
        headerStyle: { display: 'hidden' },
        style: { textAlign: 'center' },
        text: ''
    }
]

const UccSearchData = [
    {
        name: 'Pink Transportation',
        state: 'CA',
        county: 'Atlanta',
        date: '02-01-2022',
        status: 'Active',
        alert: '-',
        moreData: true
    },
    {
        name: 'Owner 1',
        state: 'CA',
        county: 'Atlanta',
        date: '02-01-2022',
        status: 'Active',
        alert: '-'
    },
    {
        name: 'Owner 2',
        state: 'CA',
        county: 'Atlanta',
        date: '02-01-2022',
        status: 'Active',
        alert: '-'
    }
]

const ApplicantFilters = [
    {
        id: 'name',
        type: 'text',
        label: 'Applicant Name',
        defaultValue: ''
    },
    {
        id: 'salesRepresentative',
        type: 'text',
        label: 'Sales Representative',
        defaultValue: ''
    },
    {
        id: 'status',
        type: 'dropdown',
        label: 'Status',
        options: [
            { value: 'New', label: 'New' },
            { value: 'Under Review', label: 'Under Review' },
            { value: 'Pending', label: 'Pending' },
            { value: 'Approved', label: 'Approved' },
            { value: 'Declined', label: 'Declined' },
            { value: 'Cancelled', label: 'Cancelled' },
            { value: 'Cautionary Approval', label: 'Cautionary Approval' },
        ],
    },
    {
        id: 'businessType',
        type: 'dropdown',
        label: 'Business Type',
        options: businessTypeOption,
    }
    /** To be used once the API recieves this query param */
    // {
    //     id: 'dateApplied',
    //     type: 'dateRange',
    //     label: 'Date Applied',
]

const clientFilter = [
    {
        id: 'entityOrganization',
        type: 'text',
        label: 'Organization Name'
    },
    {
        id: 'entityFirstName',
        type: 'text',
        label: 'First Name'
    },
    {
        id: 'entityLastName',
        type: 'text',
        label: 'Last Name'
    },
    {
        id: 'entityMiddleName',
        type: 'text',
        label: 'Middle Name'
    }
    /** To be used once the API recieves this query param */
    // {
    //     id: 'jurisdictions',
    //     type: 'text',
    //     label: 'Jurisdiction',
    //     defaultValue: ''
    // },
]

const ownersObj = {
    firstName: '',
    lastName: '',
    ssn: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip5: '',
    number: '',
    email: '',
    dateOfBirth: moment(new Date()).format('YYYY-MM-DD')
}

const OwnerDefaultForm = {
    "address": {
        "addressType": "Home"
    },
    "emails": [
        {
            "emailType": "Work",
            "primary": true
        }
    ],
    "identifications": [
        {
            "identificationType": "SSN",
            "primary": true
        }
    ],
    "phones": [
        {
            "phoneType": "Work",
            "primary": true
        }
    ]
}

const DefaultApplicationForm = {
    "business": {
        "name": "",
        "businessType": "",
        "industryType": "",
        "mcNumber": "",
        "dotNumber": "",
        "website": "",
        "startDate": "",
        "stateOfFormation": "",
        "applicationType": "Client",
        "addresses": [
            {
                "address1": "",
                "address2": "",
                "city": "",
                "state": "AL",
                "zip5": "",
                "addressType": "Work",
                "countryCode": "USA"
            },
        ],
        "identifications": [
            {
                "identification": "",
                "identificationType": "EIN",
                "primary": true
            }
        ],
        "emails": [
            {
                "email": "",
                "emailType": "Work",
                "primary": true
            }
        ],
        "phones": [
            {
                "number": "",
                "phoneType": "Work",
                "primary": true
            }
        ]
    },
    "owners": [
        {
            "firstName": "",
            "lastName": "",
            "address": {
                "address1": "",
                "address2": "",
                "city": "",
                "state": "AL",
                "zip5": "",
                "addressType": "Home",
                "countryCode": "USA"
            },
            "emails": [
                {
                    "email": "",
                    "emailType": "Work",
                    "primary": true
                }
            ],
            "identifications": [
                {
                    "identification": "",
                    "identificationType": "SSN",
                    "primary": true
                }
            ],
            "phones": [
                {
                    "number": "",
                    "phoneType": "Work",
                    "primary": true
                }
            ]
        }
    ],
    "debtor": {
        "name": "",
        "businessType": "",
        "industryType": "",
        "mcNumber": "",
        "dotNumber": "",
        "website": "",
        "startDate": "",
        "addresses": [
            {
                "address1": "",
                "address2": "",
                "city": "",
                "state": "AL",
                "zip5": "",
                "addressType": "Work",
                "countryCode": "USA"
            }
        ],
        "emails": [
            {
                "email": "",
                "emailType": "Work",
                "primary": true
            }
        ],
        "identifications": [
            {
                "identification": "",
                "identificationType": "EIN",
                "primary": true
            }
        ],
        "phones": [
            {
                "number": "",
                "phoneType": "Work",
                "primary": true
            }
        ]
    },
    salesRepresentative: { uuid: '' },
    fmcsaInteractionResponseUuid: null,
    fmcsaAuthorityUuid: null,
    fmcsaBasicUuid: null,
    fmcsaCargoCarriedUuid: null,
    fmcsaMcNumberUuid: null,
    fmcsaOperationClassificationUuid: null,
    factorCloudOnboardEnabled: null,
    motusDotSearchUuid: null,
}

const DemoModeApplicationValues = {
   client: {
     business: {
        "name": "SwiftHaul",
        "businessType": "Limited Liability Company (LLC)",
        "dbaName": "SwiftHaul Logistics",
        // Demo NPI — synthetic (1000000009), outside the range CMS normally assigns and
        // paired server-side with a fictional "DEMO SAMPLE, PROVIDER A" identity. Matches
        // HealthcareSearchDemoFixture so a demo lands on a multi-source EXCLUDED card
        // (NPPES + LEIE + SAM.gov + OFAC) without borrowing a real provider's identity.
        "npi": "1000000009",
        "industryType": "Oil and Gas",
        "mcNumber": "",
        "dotNumber": "123456",
        "website": "",
        "startDate": "2010-05-15",
        "stateOfFormation": "",
        "applicationType": "Client",
        "addresses": [
            {
                "address1": "1527 Sooner Trail",
                "address2": "",
                "city": "Oklahoma City",
                "state": "OK",
                "zip5": "73129",
                "addressType": "Work",
                "countryCode": "USA"
            },
        ],
        "identifications": [{
            "identification": "",
            "identificationType": "EIN",
            "primary": true
        }],
        "emails": [
            {
                "email": "",
                "emailType": "Work",
                "primary": true
            }
        ],
        "phones": [
            {
                "number": "111-111-1111",
                "phoneType": "Work",
                "primary": true
            }
        ]
    },
    owners: [
        {
            "firstName": "James",
            "lastName": "Calloway",
            // Demo NPI — synthetic, matches the Calloway card in HealthcareSearchDemoFixture.
            "npi": "1000000017",

            "emails": [
                {
                    "email": "rox-demo-owner@roxwrite.com",
                    "emailType": "Work",
                    "primary": true
                }
            ],
            "identifications": [
                {
                    "identification": "",
                    "identificationType": "SSN",
                    "primary": true
                }
            ],
            "phones": [
                {
                    "number": "111-111-1111",
                    "phoneType": "Work",
                    "primary": true
                }
            ],
            "address": {
                "address1": "1527 Sooner Trail",
                "address2": "",
                "city": "Oklahoma City",
                "state": "OK",
                "zip5": "73129",
                "addressType": "Home",
                "countryCode": "USA"
            },
        }
    ],
    debtor: {
        "name": "Stone Transportation",
        "dbaName": "Stone Transportation LLC",
        "businessType": "Corporations",
        "industryType": "Transportation",
        "mcNumber": "",
        "dotNumber": "999",
        "website": "",
        "startDate": "",
        "addresses": [
            {
                "address1": "20 Rock St",
                "address2": "",
                "city": "Nashville",
                "state": "TN",
                "zip5": "37201",
                "addressType": "Work",
                "countryCode": "USA"
            }
        ],
        "emails": [
            {
                "email": "",
                "emailType": "Work",
                "primary": true
            }
        ],
        "identifications": [{
            "identification": "",
            "identificationType": "EIN",
            "primary": true
        }],
        "phones": [
            {
                "number": "222-222-2222",
                "phoneType": "Work",
                "primary": true
            }
        ]
    }, salesRepresentative: {},
    fmcsaInteractionResponseUuid: null,
    fmcsaAuthorityUuid: null,
    fmcsaBasicUuid: null,
    fmcsaCargoCarriedUuid: null,
    fmcsaMcNumberUuid: null,
    fmcsaOperationClassificationUuid: null
   },
   debtor: {
     business: {
        "name": "Stone Transportation",
        "dbaName": "Stone Transportation LLC",
        "businessType": "Corporations",
        "industryType": "Transportation",
        "mcNumber": "",
        "dotNumber": "999",
        "website": "",
        "startDate": "",
        "stateOfFormation": "",
        "applicationType": "Debtor",
        "addresses": [
            {
                "address1": "20 Rock St",
                "address2": "",
                "city": "Nashville",
                "state": "TN",
                "zip5": "37201",
                "addressType": "Work",
                "countryCode": "USA"
            }
        ],
        "identifications": [{
            "identification": "",
            "identificationType": "EIN",
            "primary": true
        }],
        "emails": [
            {
                "email": "",
                "emailType": "Work",
                "primary": true
            }
        ],
        "phones": [
            {
                "number": "222-222-2222",
                "phoneType": "Work",
                "primary": true
            }
        ]
    },

    }
}

const DefaultAddress = {
    "address1": "",
    "address2": "",
    "city": "",
    "state": "AL",
    "zip5": "",
    "addressType": "Work",
    "countryCode": "USA"
}

const ReportColums = [
    {
        dataField: 'FilingNumber',
        text: 'Filing Number'
    },
    {
        dataField: 'FilingType',
        text: 'Filing Type'
    },
    {
        dataField: 'Status',
        text: 'Status',
        formatter: (cell) => {
          if(cell === 'Active') {
            return <div className='greenLabel max-w-[100px] truncate'>{cell}</div>
          }
          else if(cell === 'Inactive') {
            return <div className='redLabel'>{cell}</div>
          }
        }
    },
    {
        dataField: 'action',
        headerStyle: {
            width: '6rem',
            cursor: 'pointer'
        },
        text: ''
    }
]

const FilingDetailsColumns = [
    {
        dataField: 'FilingNumber',
        text: 'Filing Number'
    },
    {
        dataField: 'FilingType',
        text: 'Filing Type',
        style: { whiteSpace: 'initial' },
    },
    {
        dataField: 'FilingDate',
        text: 'Filling Date',
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
    {
        dataField: 'NumberOfPages',
        text: 'No of Pages',
        style: { textAlign: 'center'},
        headerStyle: {textAlign: 'center' }
    },
    {
        dataField: 'docAction',
        text: 'Document',
        style: { textAlign: 'center' },
        headerStyle: { width: '6rem' }
    },
    {
        dataField: 'action',
        text: 'Action',
        style: { textAlign: 'center' },
        headerStyle: { width: '8rem' }
    }
]

const AmendmentDetailsColumns = [
    {
        dataField: 'FilingNumber',
        text: 'Filing Number'
    },
    {
        dataField: 'FilingType',
        text: 'Filing Type',
        style: { whiteSpace: 'initial' },
    },
    {
        dataField: 'FilingDate',
        text: 'Filling Date',
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
    {
        dataField: 'NumberOfPages',
        text: 'Number of Pages',
        style: { textAlign: 'center'}
    },
    {
        dataField: 'docAction',
        text: 'Document',
        style: { textAlign: 'center' },
        headerStyle: { width: '6rem' }
    }
]

const DetailColumn = [
    {
        dataField: 'FilingNumber',
        text: 'Filing Number'
    },
    {
        dataField: 'FilingType',
        text: 'Filing Type',
        style: { whiteSpace: 'initial' },
    },
    {
        dataField: 'FilingDate',
        text: 'Filling Date',
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
    {
        dataField: 'NumberOfPages',
        text: 'Number of Pages'
    },
    {
        dataField: 'documentStatus.status',
        text: 'Document Status',
        style: { whiteSpace: 'initial' },
        formatter: (cell, row) => {
            if (row?.NumberOfPages > 0) {
                return <div>{cell ? capitalize(startCase(cell)) : 'Ready to Request'}</div>
            } else return '-'
        }
    },
    {
        dataField: 'action',
        text: 'Action',
        headerStyle: { width: '6rem' },
    }
]

const ReceivedDocumentsColumn = [
    {
        dataField: 'filingNumber',
        text: 'Filing Number'
    },
    {
        dataField: 'filename',
        text: 'filename'
    },
    {
        dataField: 'action',
        text: 'Action'
    },
]

const DebtorsColumn = [
    {
        dataField: 'Name',
        text: 'Name',
        headerStyle: {
            width: '15rem'
        }
    },
    {
        dataField: 'Address',
        text: 'Address Line',
        headerStyle: {
            width: '17rem'
        }
    },
    {
        dataField: 'City',
        text: 'City',
        headerStyle: {
            width: '9rem'
        }
    },
    {
        dataField: 'State',
        text: 'State'
    },
    {
        dataField: 'Zip',
        text: 'Zip'
    }
]

const AttachmentsColumn = [
    {
        dataField: 'fileName',
        text: 'File Name',
        formatter: (cell, row) => {
            return <div style={{ overflow: "hidden", textOverflow: "ellipsis", cursor:"default" }} title={cell} className='text-ellipsis'>{cell}</div>
        },
    },
    {
        dataField: 'purpose',
        text: 'Note',
        style: { whiteSpace: 'initial', wordBreak: 'break-all' },
    },
    {
        dataField: "roxFileType",
        text: "File Category",
        formatter: (cell) => {
            return ROX_FILE_TYPE_CATEGORY_BY_ENUM[cell] || cell
        },
    },
    {
        dataField: 'fileType',
        text: 'File Type',
        formatter: (cell, row) => {
            return <div style={{ overflow: "hidden", textOverflow: "ellipsis", cursor:"default" }} title={cell} className='text-ellipsis'>{cell}</div>
        },
    },
    {
        dataField: 'fileSize',
        text: 'File Size',
        formatter: cell => cell ? `${(cell / 1024 / 1024).toFixed(2)} MB` : emptyFormatter()
    },
    {
        dataField: 'createdBy.name',
        text: 'Created By',
        formatter: (cell, row) => {
            return <div style={{ overflow: "hidden", textOverflow: "ellipsis", cursor:"default" }} title={cell} className='text-ellipsis'>{cell}</div>
        },
    },
    {
        dataField: 'createdAt',
        text: 'Created At',
        formatter: (cell) => {
            const formattedDate = moment(cell).format('Do MMM YYYY');
            return (
                <div
                    style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "default" }}
                    title={formattedDate}
                    className="text-ellipsis"
                >
                    {formattedDate}
                </div>
            );
        }
    },
    {
        dataField: 'action',
        headerStyle: { display: "hidden" },
        text: ''
        }
]
export const AttachmentsFilters = [
    {
        id: 'roxFileType',
        type: 'dropdown',
        label: 'File Category',
        options: [
            { value: 'UCC', label: 'UCC' },
            { value: 'Cobalt', label: 'Cobalt' },
            { value: 'ROX Report', label: 'ROX Report' },
            { value: 'Gmail File', label: 'Gmail File' },
            { value: 'LexisNexis', label: 'LexisNexis' },
            { value: 'User Upload', label: 'User Upload' },
            { value: 'Client Portal', label: 'Client Portal' }
        ],
    }
]

export const ROX_FILE_TYPE_CATEGORY_BY_ENUM = {
    UCC_DOCUMENT: "UCC",
    COBALT_SOS_SCREENSHOT: "Cobalt",
    UCC_ROX_REPORT: "ROX Report",
    GMAIL_FILE: "Gmail File",
    FMCSA_ROX_REPORT: "ROX Report",
    UCC_FILING_DETAILS_ROX_REPORT: "ROX Report",
    UCC_FILING_IMAGE: "UCC",
    UCC_FILING_E_FILE: "UCC",
    LEXIS_PDF_OWNER: "LexisNexis",
    LEXIS_PDF_BUSINESS: "LexisNexis",
    LEXIS_PDF_INSTANT_ID: "LexisNexis",
    USER_FILE: "User Upload",
    TO_SUBMIT_UCC_FILE: "UCC",
    CREDIT_MEMO:"ROX Report",
    CREDIT_MEMO_APPROVED:"ROX Report",
    CLIENT_PORTAL_FILES:"Client Portal",
};

const CreditCheckColumn = [
    {
        dataField: 'name',
        text: 'Name',
        headerStyle: {
            minWidth: '15rem'
        },
        style: { display: 'flex' },
        formatter: (cell, row) => {
            return <>
                {
                    row.entityType === 'BUSINESS_DBA' &&
                    <PrepareTooltip
                        theme='white'
                        title='Doing Business As'
                        placement='top'
                    >
                        <Icon folder='icons' fileName='DBA.svg' className='mr-2' />
                    </PrepareTooltip>
                }
                <div style={row.entityType === 'BUSINESS_DBA' ? { marginTop: '1.5px' } : {}} title={cell}>{cell}</div>
            </>
        }
    },
    {
        dataField: 'state',
        text: 'State',
        formatter: emptyFormatter
    },
    {
        dataField: 'saveDate',
        text: 'Saved Date',
        formatter: momentDateFormatter
    },
    {
        dataField: 'status',
        text: 'Status',
        formatter: (cell) => {
            if (!cell) return emptyFormatter()
            if (cell === strings.STATUS_NAMES.Hit_Completed) return 'Completed'
            // The search has finished; the report just hasn't been saved. "In-Progress" read as still loading.
            if (cell === strings.STATUS_NAMES.In_Progress) return (
                <Tooltip title='The search finished, but no report has been saved for it yet.' placement='top' arrow>
                    <span style={{ cursor: 'help' }}>Not saved</span>
                </Tooltip>
            )
            return cell
        }
    },
    {
        dataField: 'requestedBy',
        text: 'Requested By',
        headerStyle: { width: '10rem' },
        formatter: (cell) => {
            if (cell) {
                return (
                    <PrepareTooltip
                        theme='white'
                        title={cell}
                        placement='bottom'
                    >
                        <div style={{ maxWidth: '9rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {cell}
                        </div>
                    </PrepareTooltip>
                )
            }
            return '---'
        }
    },
    {
        dataField: 'action',
        headerStyle: { display: "hidden" },
        text: ''
    }
]

const creditReportColumns = [
    {
        dataField: 'name',
        text: 'Name',
        headerStyle: {
            minWidth: '15rem'
        },
        style: {
            whiteSpace: 'initial'
        }
    },
    {
        dataField: 'address.simpleValue',
        text: 'address',
        headerStyle: {
            minWidth: '15rem'
        },
        style: {
            whiteSpace: 'initial'
        },
        formatter: emptyFormatter
    },
    {
        dataField: 'country',
        text: 'Country',
        headerStyle: {
            width: '4rem'
        },
        formatter: emptyFormatter
    },
    {
        dataField: 'action',
        headerStyle: { display: 'hidden' }
    }
]

const TradePaymentSummaryColumns = [
    {
        dataField: 'totalTradesNumber',
        text: 'Total Trades',
    },
    {
        dataField: 'totalTradesBalance',
        text: 'Balance',
    },
    {
        dataField: 'currency',
        text: 'Currency',
    },
    {
        dataField: 'continuousTradesNumber',
        text: 'Continuous Trades',
    },
    {
        dataField: 'recentHighCredit',
        text: 'Recent High Credit',
    },
]

const FilingSummaryColumns = [
    {
        dataField: 'type',
        text: 'Type',
        headerStyle: { width: '8rem' },
        style: { whiteSpace: 'initial' }
    },
    {
        dataField: 'bankruptcy',
        text: 'Bankruptcy',
    },
    {
        dataField: 'taxLienFilings',
        text: 'Tax Lien Filings',
    },
    {
        dataField: 'judgmentFilings',
        text: 'Judgment Filings',
    },
    {
        dataField: 'sum',
        text: 'Total',
        formatter: (cell, row) => {
            const { value, currency } = row.sum || {}
            return <div>{value} {currency}</div>
        }
    },
    {
        dataField: 'uccFilings',
        text: 'Ucc Filings',
    },
    {
        dataField: 'cautionaryUCCFilings',
        text: 'Cautionary UCC Filings',
    },
    {
        dataField: 'suits',
        text: 'Suits',
    },
]

const TradeLinesInformationColumn = [
    {
        dataField: 'averageCreditAmount',
        text: 'Average Credit Amount',
    },
    {
        dataField: 'industryPerformance',
        text: 'Performance',
    },
    {
        dataField: 'creditUtilization',
        text: 'Credit Utilization',
    },
    {
        dataField: 'lastUpdateDate',
        text: 'Updated Date',
        formatter: (cell) => cell ? <Moment format="MM/DD/YYYY">{cell}</Moment> : ''
    },
    {
        dataField: 'currency',
        text: 'Currency',
    },
]


const BranchOfficesColumn = [
    {
        dataField: 'name',
        text: 'Name',
        style: {
            whiteSpace: 'initial'
        }
    },
    {
        dataField: 'branchType',
        text: 'Branch Type',
    },
    {
        dataField: 'address.simpleValue',
        text: 'Address',
        style: {
            whiteSpace: 'initial'
        }
    },
    {
        dataField: 'address.country',
        text: 'Country',
    },
]

const tradePaymentsByCreditExtended = [
    {
        dataField: 'credit',
        text: 'Credit',
        headerStyle: { minWidth: '5rem' },
        style: {
            whiteSpace: 'initial'
        }
    },
    {
        dataField: 'currency',
        text: 'Currency',
    },
    {
        dataField: 'pastDue',
        text: 'Past Due',
    },
    {
        dataField: 'percentWithinTerms',
        text: 'Percent With In Terms',
    },
    {
        dataField: 'totalAmount',
        text: 'Total Amount',
    },
    {
        dataField: 'tradeLines',
        text: 'Trade Lines',
    }
]

const LifetimeHighBalanceChartColumns = [
    {
        dataField: 'duration',
        text: 'Duration',
    },
    {
        dataField: 'dbt',
        text: 'DBT',
    },
    {
        dataField: 'singleHighBalance',
        text: 'Single High Balance',
    },
    {
        dataField: 'tradeLinesHighBalance',
        text: 'Trade Lines High Balance',
    },
    {
        dataField: 'averageHighBalance',
        text: 'Average High Balance',
    },
    {
        dataField: 'currency',
        text: 'Currency',
    }
]


const uccFilingColumns = [
    {
        dataField: 'OriginFilingNumber',
        text: 'Filing Number',
        headerStyle: { width: '10rem' },
        style: { whiteSpace: 'initial' },
        formatter: (cell, row) => {
            return <div>{cell} {`(${row.FilingJurisdiction})`}</div>
        }
    },
    {
        dataField: 'OriginFilingDate',
        text: 'Filing Date',
        headerStyle: { width: '7rem' },
        formatter: (cell) => {
            if (cell && cell.Month && cell.Day && cell.Year)
                return <div>{cell.Month}/{cell.Day}/{cell.Year}</div>
            return emptyFormatter()
        }
    },
    {
        dataField: 'Filings.Filing',
        text: 'Filing Types',
        style: { whiteSpace: 'initial' },
        formatter: (cell) => {
            if (cell && cell?.length)
                return <div>{cell.map(data => data.Type).join(', ')}</div>
            return emptyFormatter()
        }
    },
    {
        dataField: 'Collaterals.Collateral[0].Description',
        text: 'Collateral',
        style: { whiteSpace: 'initial', wordBreak: 'break-all' },
        formatter: emptyFormatter
    },
    {
        dataField: 'Action1',
        text: 'Debtors',
    },
    {
        dataField: 'Action2',
        text: 'Secureds',
    },
]


const infoColumns = [
    {
        dataField: 'Name',
        text: 'Name',
        style: { whiteSpace: 'initial' },
        formatter: (cell) => {
            return <div>{cell.First} {cell.Middle} {cell.Last}</div>
        }
    },
    {
        dataField: 'Address',
        text: 'Address',
        style: { whiteSpace: 'initial' },
        formatter: (cell) => {
            return <div>{cell.StreetNumber} {cell.StreetName}, {cell.City}, {cell.State}</div>
        }
    },
    {
        dataField: 'Deceased',
        text: 'Deceased',
    },
    {
        dataField: 'DOB',
        text: 'DOB',
        formatter: (cell) => {
            if (cell)
                return <div>{cell.Month}/{cell.Day}/{cell.Year}</div>
            return emptyFormatter()
        }
    },
    {
        dataField: 'Phones.Phone[0].Phone10',
        text: 'Phone',
        formatter: emptyFormatter
    },
]

const continuationColumns = [
    {
        dataField: 'applicationName',
        text: 'Application Name',
        headerStyle: { width: '15rem' },
    },
    {
        dataField: 'debtorName',
        text: 'Debtor Name',
        headerStyle: { width: '15rem' },
    },
    {
        dataField: 'additionalData.filingNumber',
        text: 'Filing Number',
    },
    {
        dataField: 'actionItemType',
        text: 'Status',
    },
    {
        dataField: 'dueDate',
        text: 'Due Date',
        formatter: (cell) => cell ? <Moment format="MM/DD/YYYY">{cell}</Moment> : emptyFormatter()
    },
    {
        dataField: 'action',
        text: 'Actions',
        headerStyle: { width: '10rem' }
    }
]

const amendmentsColumn = [
    {
        dataField: 'debtorName',
        text: 'Debtor Name',
        headerStyle: { width: '15rem' },
        formatter: emptyFormatter
    },
    {
        dataField: 'fileNumber',
        text: 'Filing Number',
        formatter: emptyFormatter
    },
    {
        dataField: 'amendmentType',
        text: 'Type',
    },
    {
        dataField: 'lapseDate',
        text: 'Due Date',
        formatter: (cell) => cell ? <Moment format="MM/DD/YYYY">{cell}</Moment> : emptyFormatter()
    },
    {
        dataField: 'amendmentDate',
        text: 'Amendment Date',
        formatter: (cell) => cell ? <Moment format="MM/DD/YYYY">{cell}</Moment> : emptyFormatter()
    },
    {
        dataField: 'action',
        text: 'Actions',
        headerStyle: { width: '7rem' }
    }
]

const activeColumns = (hideToolTip = false) => [
    {
        dataField: 'debtorName',
        text: 'Debtor Name',
        headerStyle: { width: '15rem' },
        style: { whiteSpace: 'initial' },
        sort: true,
        formatter: (cell, row) => {
            if (!row?.entities?.length)
                return emptyFormatter()

            const debtors = row.entities.filter((entity, i) => entity.entityType === 'DEBTOR')
            if (debtors.length === 1)
                return <div title={cell}>{cell}</div>
            if (debtors.length > 1 && !hideToolTip)
                return (
                    <div className='d-flex'>
                        <div title={cell}>{cell}</div>
                        <PrepareTooltip
                            theme='white'
                            title={<>
                                {debtors.map((entity, i) =>
                                    i ? <div key={i} className='grouped-applications-name'>{entity.name}</div> : null
                                )}
                            </>}
                        >
                            <span className='grouped-applications'>+{debtors.length - 1}</span>
                        </PrepareTooltip>
                    </div>
                )
        }
    },
    {
        dataField: 'filingType',
        text: 'Filing Type',
        formatter: (cell) => cell ? <div title={cell}>{cell}</div> : emptyFormatter(),
        sort: false
    },
    {
        dataField: 'filingNumber',
        text: 'Filing Number',
        formatter: (cell) => cell ? <div title={cell}>{cell}</div> : emptyFormatter(),
        sort: true
    },
    {
        dataField: 'jurisdiction',
        text: 'Jurisdiction',
        formatter: (cell) => cell ? <div title={cell}>{cell}</div> : emptyFormatter(),
        sort: true
    },
    {
        dataField: 'creationDate',
        text: 'Submission Date',
        headerStyle: { width: '12rem' },
        formatter: (cell) => cell ? fcsDatesFormatter(cell) : emptyFormatter(),
        sort: true
    },
    {
        dataField: 'fileDate',
        text: 'Filing Date',
        formatter: (cell) => cell ? fcsDatesFormatter(cell) : emptyFormatter(),
        sort: true
    },
    {
        dataField: 'lapseDate',
        text: 'Lapse Date',
        formatter: (cell) => cell ? fcsDatesFormatter(cell) : emptyFormatter(),
        sort: true
    },
    {
        dataField: 'amendmentAction',
        text: 'Amendment Action',
        formatter: (cell, { amendmentType }) => {
            if (cell && amendmentType) {
                const value = changeTypeLabels[cell.toLowerCase().includes('no') ? amendmentType : cell]
                return <div title={value}>{value}</div>
            }
            return emptyFormatter
        },
        sort: false
    },
    {
        dataField: 'status',
        text: 'Status',
        formatter: (cell) => cell ? <div title={cell}>{cell}</div> : emptyFormatter(),
        sort: false
    },
    {
        dataField: 'action',
        text: 'Actions',
        headerStyle: { width: '10rem' },
        sort: false
    }
]

const monitoringColumns = [
    {
        dataField: 'applicationName',
        text: 'Application',
        headerStyle: { width: '10rem' },
        sort: false,
        formatter: (cell, row) => {
            return (
                <Link className={'id-hyper-link'}
                    to={`/applicant/${row.applicationId}`}
                >
                    {cell}
                </Link>
            )
        }
    },
    {
        dataField: 'monitoringReports',
        text: 'Monitoring Reports',
        headerStyle: { width: '10rem' },
        formatter: (cell) => cell ? <div title={cell}>
            <PrepareTooltip
                theme="white"
                title={
                    <ul style={{ margin: 0, padding: '0 1rem' }}>
                        {Array.isArray(cell) && cell.map((item, idx) => (
                            <li key={idx} style={{ listStyle: 'disc' }}>{item}</li>
                        ))}
                    </ul>
                }
            >
                <div style={{ overflow: "hidden", textOverflow: "ellipsis", cursor: "default" }} className='text-ellipsis'>{Array.isArray(cell) ? cell.join(', ') : cell}</div>
            </PrepareTooltip>
        </div> : emptyFormatter(),
        sort: false
    },
    {
        dataField: 'alertsCount',
        text: 'All Alerts',
        formatter: (cell) => cell ? <div title={cell}>{cell}</div> : emptyFormatter(),
        sort: false
    },
    {
        dataField: 'reportsCount',
        text: 'Reports#',
        formatter: (cell) => cell ? <div title={cell}>{cell}</div> : emptyFormatter(),
        sort: false
    },
    {
        dataField: 'lastUpdated',
        text: 'Last Updated',
        headerStyle: { width: '8rem' },
        formatter: momentDateFormatter,
        sort: false
    },
    {
        dataField: 'monitoringStartDate',
        text: 'Monitoring Start Date',
        headerStyle: { width: '12rem' },
        formatter: momentDateFormatter,
        sort: false
    },
    {
        dataField: 'monitoringEndDate',
        text: 'End Date',
        formatter: momentDateFormatter,
        sort: false
    },
    {
        dataField: 'monitoringEnabled',
        text: 'Monitoring',
        formatter: (cell) =>
            cell === true
                ? <div title="Enabled">Enabled</div>
                : <div title="Disabled">Disabled</div>,
        sort: false
    },
]

const buisnessBgvColumns = [
    {
        dataField: 'Best.CompanyNameInfo.CompanyName',
        text: 'Company Name',
        headerStyle: { minWidth: '15rem' },
        style: { whiteSpace: 'initial' },
        // alreadyRequested is set by the backend for candidates that already have a detailed report
        // (saved or not), which are reused without a new charge. Shown under the name with the date.
        formatter: (cell, row) => (
            <>
                <div>{cell || emptyFormatter()}</div>
                {row.alreadyRequested && (
                    <span className='globalLabel' style={{ background: 'var(--badge-watch-bg)', color: 'var(--badge-watch-ink)', display: 'inline-block', marginTop: 6 }}>
                        {row.lastRequestedDate
                            ? <>{strings.APPLICANT.BGV.REQUESTED_ON} <Moment format="MM/DD/YYYY">{row.lastRequestedDate}</Moment></>
                            : strings.APPLICANT.BGV.ALREADY_REQUESTED}
                    </span>
                )}
            </>
        )
    },
    {
        dataField: 'Best.AddressInfo.Address',
        text: 'Address',
        headerStyle: { minWidth: '15rem' },
        style: { whiteSpace: 'initial' },
        formatter: (cell) => cell ? `${cell.StreetNumber || ''} ${cell.StreetSuffix || ''} ${cell.StreetName || ''}, ${cell.City || ''}, ${cell.State || ''}, ${cell.Zip5 || ''}` : emptyFormatter()
    },
    {
        dataField: 'action',
        headerStyle: { display: 'hidden' },
        text: ''
    },
    {
        dataField: 'verified',
        headerStyle: {
            display: 'hidden',
            width: '7.5rem'
        },
        text: '',
        formatter: (cell) => {
            return cell ?
                <span className='globalLabel' style={{ background: 'var(--badge-approved-bg)', color: 'var(--badge-approved-ink)' }}>{strings.APPLICANT.BGV.VERIFIED}</span>
                :
                null
        }
    }
]

const bgvSummaryColums = [
    {
        dataField: 'Best.CompanyNameInfo.CompanyName',
        text: 'Company Name',
        headerStyle: {
            width: isScreenSmall() ? '15rem' : '20rem'
        },
        style: { whiteSpace: 'initial' },
        formatter: emptyFormatter
    },
    {
        dataField: 'Best.AddressInfo.Address',
        text: 'Address',
        headerStyle: { minWidth: '15rem' },
        style: { whiteSpace: 'initial' },
        formatter: (cell) => cell ? `${cell.StreetNumber || ''} ${cell.StreetSuffix || ''} ${cell.StreetName || ''}, ${cell.City || ''}, ${cell.State || ''}, ${cell.Zip5 || ''}` : emptyFormatter()
    },
    {
        dataField: 'action',
        headerStyle: {
            display: 'hidden',
            width: '10rem'
        },
        text: ''
    },
]


const BTOwnersColumn = [
    {
        dataField: 'name',
        text: 'Owner',
        key: "ownerId",
        headerStyle: { width: '30%' },
        formatter: (cell, row) => (
            <div>
                <div>{cell}</div>
                <div style={{ fontSize: 12, color: row.ownerEmail ? 'var(--fg-tertiary)' : 'var(--status-declined)' }}>
                    {row.ownerEmail || 'No email'}
                </div>
            </div>
        )
    },
    {
        dataField: 'status',
        text: 'Status'
    },
    {
        dataField: 'requestedAt',
        text: 'Requested at',
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
    {
        dataField: 'grantedAt',
        text: 'Granted at',
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
    {
        dataField: 'emails[0].sentAt',
        text: 'Last Email Sent at',
        headerStyle: { whiteSpace: 'normal' },
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
]

function mapBankTransactionData(data) {
    return [
        {
            title: 'Name :',
            value: getValueFromData(data, 'name')
        },
        {
            title: 'Original Description',
            value: getValueFromData(data, 'originalDescription')
        },
        {
            title: 'Amount :',
            value: getValueFromData(data, 'amount')
        },
        {
            title: 'ISO Currency Code :',
            value: getValueFromData(data, 'isoCurrencyCode')
        },
        {
            title: 'Categories :',
            value: getValueFromData(data, 'categories')
        },
        {
            title: 'Payment Channel :',
            value: getValueFromData(data, 'paymentChannel')
        },
        {
            title: 'Date :',
            value: getValueFromData(data, 'date')
        },
        {
            title: 'Authorized Date :',
            value: getValueFromData(data, 'authorizedDate')
        }
    ]
}

function mapRecurrentBankTransactionData(data) {
    return [
        {
            title: 'Description :',
            value: getValueFromData(data, 'description')
        },
        {
            title: 'Frequency :',
            value: getValueFromData(data, 'frequency')
        },
        {
            title: 'Categories :',
            value: getValueFromData(data, 'categories')
        },
        {
            title: 'Average Amount :',
            value: getValueFromData(data, 'averageAmount')
        },
        {
            title: 'Last Amount :',
            value: getValueFromData(data, 'lastAmount')
        },
        {
            title: 'First Date :',
            value: getValueFromData(data, 'firstDate')
        },
        {
            title: 'Last Date :',
            value: getValueFromData(data, 'lastDate')
        },
        {
            title: 'Status :',
            value: getValueFromData(data, 'status')
        }
    ]
}

function mapAccountSummary(data) {
    return [
        {
            title: 'Current Balance',
            current: getValueFromData(data.accountBalance, 'currentTotal'),
            diff: getValueFromData(data.accountBalance, 'lastMonthPercentageDifference'),
        },
        {
            title: 'Total Outgoing Payments',
            current: getValueFromData(data.totalOutgoingPayments, 'currentTotal'),
            diff: getValueFromData(data.totalOutgoingPayments, 'lastMonthPercentageDifference'),
        },
        {
            title: 'Total Recurring Transactions',
            current: getValueFromData(data.totalRecurringTransactions, 'currentTotal'),
            diff: getValueFromData(data.totalRecurringTransactions, 'lastMonthPercentageDifference', true),
        },
    ]
}

const transactionColumn = [
    {
        dataField: 'name',
        text: 'Name',
        formatter: (cell) => {
            return <div>{cell}</div>
        }
    },
    {
        dataField: 'categories',
        text: 'Categories',
        formatter: (cell) => {
            if (cell && cell?.length)
                return <div>{cell.join(', ')}</div>
            return emptyFormatter()
        }
    },
    {
        dataField: 'direction',
        text: 'D/C',
        formatter: (cell) => {
            return <div>{cell}</div>
        }
    },
    {
        dataField: 'date',
        text: 'Date',
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
    {
        dataField: 'amount',
        text: 'Amount',
        headerStyle: {
            textAlign: 'center',
            width: '11rem'
        },
        formatter: (cell) => {
            return cell ? amountsFormatter(cell) : emptyFormatter()
        }
    },
    {
        dataField: 'action',
        headerStyle: { display: "hidden" },
        text: ''
    }
]

const recurrentTransactionColumn = [
    {
        dataField: 'description',
        text: 'Description',
        formatter: formatTooltip
    },
    {
        dataField: 'flow',
        text: 'Direction',
        formatter: (cell) => {
            return <div>{cell}</div>
        }
    },
    {
        dataField: 'status',
        text: 'Status',
        formatter: (cell) => {
            return <div>{cell}</div>
        }
    },
    {
        dataField: 'frequency',
        text: 'Frequency',
        formatter: (cell) => {
            if (cell) return <div>{cell}</div>
            return emptyFormatter()
        }
    },
    {
        dataField: 'categories',
        text: 'Categories',
        formatter: formatTooltip
    },
    {
        dataField: 'firstDate',
        text: 'First Date',
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
    {
        dataField: 'averageAmount',
        text: 'Average Amount',
        headerStyle: {
            textAlign: 'center',
            width: '11rem'
        },
        formatter: (cell) => {
            return cell ? amountsFormatter(cell) : emptyFormatter()
        }
    },
    {
        dataField: 'numberOfTransactions',
        headerStyle: { display: "hidden" },
        text: '',
        formatter: (cell) => {
            return <div>{cell} transactions</div>
        }
    },
    {
        dataField: 'action',
        headerStyle: { display: "hidden" },
        text: ''
    }
]

const transRTDataColumn = [
    {
        dataField: 'date',
        text: 'Date',
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
    {
        dataField: 'amount',
        text: 'Amount',
        headerStyle: {
            textAlign: 'center',
            width: '11rem'
        },
        formatter: (cell) => {
            return cell ? amountsFormatter(cell) : emptyFormatter()
        }
    },
    {
        dataField: 'paymentChannel',
        text: 'Payment Channel',
        formatter: (cell) => {
            return <div>{cell}</div>
        }
    },
    {
        dataField: 'action',
        headerStyle: { display: "hidden" }
    }
]

const UccDocumentRequestColumn = [
    {
        dataField: 'initialFilingNumber',
        text: 'Initial Filing',
        headerStyle: {
            width: '10rem',
            backgroundColor: '#253837',
            color: 'white'
        },
    },
    {
        dataField: 'filingNumber',
        text: 'Filing Requested',
        headerStyle: {
            width: '10rem',
            backgroundColor: '#253837',
            color: 'white'
        },
    },
    {
        dataField: 'numberOfPages',
        text: 'Pages',
        headerStyle: {
            width: '10rem',
            backgroundColor: '#253837',
            color: 'white'
        },
    },
]

const DocumentRequestColumn = [
    {
        dataField: 'initialFiling',
        text: 'Initial Filing',
        headerStyle: {
            width: '10rem',
            backgroundColor: '#253837',
            color: 'white'
        },
    },
    {
        dataField: 'filingNumber',
        text: 'File Requested',
        headerStyle: {
            width: '10rem',
            backgroundColor: '#253837',
            color: 'white'
        },
    },
    {
        dataField: 'noOfPages',
        text: 'pages',
        headerStyle: {
            width: '10rem',
            backgroundColor: '#253837',
            color: 'white'
        },
    },
]

const uccSummaryColumns = [
    {
        dataField: 'filingNumber',
        text: 'Filing Number',
    },
    {
        dataField: 'filingType',
        text: 'Filing Type',
        formatter: (cell, row) => {
            if (!cell || !cell.length){
                return emptyFormatter()
            }
            function isSupportedFilingType() {
                return cell === 'UCC' || cell === 'STL' || cell === 'FTL';
            }
            function filingTypeToolTipValue() {
                switch (cell) {
                    case 'UCC':
                        return 'Uniform Commercial Code';
                    case 'STL':
                        return 'State Tax Lien';
                    case 'FTL':
                        return 'Federal Tax Lien';
                    default:
                        return cell;
                }
            }

            if (isSupportedFilingType()){
                return (
                    <div className='d-flex'>
                        <PrepareTooltip theme="white" title={filingTypeToolTipValue(cell)}>
                            {cell}
                        </PrepareTooltip>
                    </div>
                )
            }
            return <div title={cell}>{cell}</div>
        }
    },
    {
        dataField: 'provider',
        text: 'Provider',
    },
    {
        dataField: 'state',
        text: 'State',
    },
    {
        dataField: 'status',
        text: 'Status',
    },
    {
        dataField: 'filingDate',
        text: 'Date Filed',
    },
    {
        dataField: 'action',
        headerStyle: { display: "hidden" },
        text: ''
    }
]

const sosSearchesColumnsQS = [
    {
        dataField: 'payload.searchQuery',
        text: 'Search Query',
        headerStyle: {
            minWidth: '15rem'
        },
        style: { display: 'flex' },
        formatter: emptyFormatter
    },
    {
        dataField: 'createdDate',
        text: 'Created Date',
        formatter: (cell) => cell ? <Moment format="MM/DD/YYYY">{cell}</Moment> : ''
    },
    {
        dataField: 'payload.state',
        text: 'State',
        formatter: upperCaseFormatter
    },
    {
        dataField: 'status',
        text: 'Status',
        formatter: emptyFormatter
    },
    {
        dataField: 'action',
        headerStyle: { display: "hidden" },
        text: ''
    }
]
// Old Header ================================================================================================================
const BuisnessColumns = withEmptyFormatter([
    {
        dataField: 'businessType',
        text: 'Business Type',
        headerStyle: { width: '10rem' },
    },
    {
        dataField: 'identifications[0].identification',
        text: 'Ein'
    },
    {
        dataField: 'addresses[0].address1',
        text: 'Address',
        style: {
            whiteSpace: 'initial'
        },
        formatter: (cell, row) => {
            if (!row?.addresses?.length)
                return emptyFormatter()

            const addresses = row.addresses

            if (addresses.length === 1)
                return <div title={cell}>{cell}</div>
            if (addresses.length > 1)
                return (
                    <div className='d-flex'>
                        <div title={cell}>{cell}</div>
                        <PrepareTooltip
                            theme='white'
                            title={<>
                                {addresses.map((address, i) =>
                                    i ? <div key={i} className='grouped-applications-name'>{address.address1}</div> : null
                                )}
                            </>}
                        >
                            <span className='grouped-applications'>+{addresses.length - 1}</span>
                        </PrepareTooltip>
                    </div>
                )
        },
        headerStyle: { width: '15rem' },
    },
    {
        dataField: 'salesRepresentative',
        text: 'Sales Representative',
        headerStyle: { width: '13rem' },
        style: { whiteSpace: 'initial' },
        formatter: (cell, row) => {
            if (cell)
                return `${cell.firstName} ${cell.lastName}`
            return emptyFormatter()
        }
    },
    {
        dataField: 'emails[0].email',
        text: 'Email',
        headerStyle: {
            width: '10rem'
        },
        style: {
            whiteSpace: 'initial'
        }
    },
    {
        dataField: 'website',
        text: 'Website',
        style: {
            whiteSpace: 'initial'
        },
        formatter: emptyFormatter
    },
    {
        dataField: 'phones[0].number',
        text: 'Phone'
    },
    {
        dataField: 'addresses[0].state',
        text: 'State'
    },
    {
        dataField: 'action',
        headerStyle: { display: "hidden", width: "4rem" },
        text: ''
    }
])

const OwnersColumn = withEmptyFormatter([
    {
        dataField: 'FilingType',
        text: 'Owner',
        headerStyle: {
            width: '15rem'
        },
        formatter: (cell, row) => {
            const { firstName = '', lastName = '' } = row
            return <div title={`${firstName} ${lastName}`}>{firstName} {lastName}</div>
        }
    },
    {
        dataField: 'dateOfBirth',
        text: 'Dob',
        formatter: (cell) => {
            return dateFormatter(cell)
        }
    },
    {
        dataField: 'phones[0].number',
        text: 'Phone'
    },
    {
        dataField: 'identifications[0].identification',
        text: 'SSN'
    },
    {
        dataField: 'address.address1',
        text: 'Address'
    },
    {
        dataField: 'address.state',
        text: 'State'
    },
    {
        dataField: 'address.zip5',
        text: 'Zip'
    },
    {
        dataField: 'action',
        headerStyle: { display: "hidden", width: "4rem" },
        text: ''
    }
])
// ===========================================================================================================================

const mapSearchResultInfo = (searchResult, possibleAlternatives) => {
    const alternatives = possibleAlternatives?.length > 0 ? possibleAlternatives.length : 'None';
    return [
        {
            title: 'Entity Type',
            value: getValueFromData(searchResult, 'entityType')
        },
        {
            title: 'Physical Address',
            value: joinAttributes({ street: searchResult?.physicalAddressStreet, city: searchResult?.physicalAddressCity, state: searchResult?.physicalAddressState, zip: searchResult?.physicalAddressZip })
        },
        {
            title: 'State of SOS Registration',
            value: getValueFromData(searchResult, 'stateOfSosRegistration')
        },
        {
            title: 'Possible Alternatives',
            value: alternatives
        },
        {
            title: 'Filing Date',
            value: getValueFromData(searchResult, 'filingDate')
        },
        {
            title: 'State of Formation',
            value: getValueFromData(searchResult, 'stateOfFormation')
        }
    ]
}

const sosRecordFilingHistoryColumns = [
    {
        text: strings.APPLICANT.SOS.FILING_TYPE,
        dataField: 'name',
        formatter: emptyFormatter
    },
    {
        text: strings.APPLICANT.SOS.FILING_DATE,
        dataField: 'url',
        formatter: (cell, row) => {
            return <p onClick={() => window.open(cell, '_blank')} className='linkDocument'>
                Link Document
            </p>
        }
    },
    {
        text: strings.APPLICANT.SOS.FILING_DATE,
        dataField: 'date',
        formatter: momentOnlyDateFormatter
    },
]

const mapBusinessInformation = (data) => {
    return [
        {
            title: strings.APPLICANT.SOS.BUSINESS_NAME,
            value: () => <p>{getValueFromData(data, 'title')}</p>
        },
        {
            title: strings.APPLICANT.SOS.STATUS,
            value: () => {
                var status = getValueFromData(data, 'normalizedStatus');
                var labelColor = status.includes('Active')? 'greenLabel' : 'redLabel';

             return <div className={labelColor}>{status}</div>
            }

        },
        {
            title: strings.APPLICANT.SOS.FILING_DATE,
            value: () => <p>{getValueFromData(data, 'filingDate')}</p> // TODO: format date, confirm same this is equal to date of formation?
        },
        {
            title: strings.APPLICANT.SOS.STATE_OF_FORMATION,
            value: () => <p>{getValueFromData(data, 'stateOfFormation')}</p>
        },
        {
            title: strings.APPLICANT.SOS.STATE_OF_SOS_REGISTRATION,
            value: () => <p>{getValueFromData(data, 'stateOfSosRegistration')}</p>
        },
        {
            title: strings.APPLICANT.SOS.ENTITY_TYPE,
            value: () => <p>{getValueFromData(data, 'entityType')}</p>
        },
        {
            title: strings.APPLICANT.SOS.PHYSICAL_ADDRESS,
            value: () => <p>{joinAttributes({ street: data?.physicalAddressStreet, city: data?.physicalAddressCity, state: data?.physicalAddressState, zip: data?.physicalAddressZip })}</p>
        },
        {
            title: strings.APPLICANT.SOS.SOS_LINK,
            value: () => {
                if (data?.url) {
                    return (<p onClick={() => window.open(data?.url, '_blank')} className='linkDocument'>
                            {strings.APPLICANT.SOS.OPEN_SOS_WEBSITE}
                        </p>)
                }
                return '-'
            }
        },
        {
            title: strings.APPLICANT.SOS.SCREENSHOT_URL,
            value: () => {
                if (data?.screenshotUrl) {
                    return (<p onClick={() => window.open(data?.screenshotUrl, '_blank')} className='linkDocument'>
                    {strings.APPLICANT.SOS.DOWNLOAD}
                    </p>)
                }
                return '-'
            }
        },
        {
            title: strings.APPLICANT.SOS.MESSAGES,
            value: () => {
                if (data?.messages) {
                    return data.messages.map((message, index) => (
                        <p key={index} className='message'>
                            {message}
                        </p>
                    ))
                }
                return '-'
            }
        }
    ]
}

const mapRegisteredAgentInformation = (data) => {
    return [
        {
            title: strings.APPLICANT.SOS.REGISTERED_AGENT_NAME,
            value: () => <p>{getValueFromData(data, 'agentName')}</p>
        },
        {
            title: strings.APPLICANT.SOS.PHYSICAL_ADDRESS,
            value: () => <p>{joinAttributes({ street: data?.physicalAddressStreet, city: data?.physicalAddressCity, state: data?.physicalAddressState, zip: data?.physicalAddressZip })}</p>
        }
    ]
}

const MAX_RETRIES_FOR_SOS_SEARCH = 24

const buildUccDefaultFormValues = (pastParameters, selectedEntity) => {
    const dataToUse = pastParameters ? pastParameters : selectedEntity
    return {
        defaultValues: {
            searchOrganizationName: dataToUse.searchOrganizationName || '',
            searchFirstName: dataToUse.searchFirstName || '',
            searchLastName: dataToUse.searchLastName || '',
            stateCode: dataToUse.stateCode || dataToUse.state || '',
            date: pastParameters ? moment(pastParameters.date).format('YYYY-MM-DD') : moment().subtract(6, 'years').format('YYYY-MM-DD'),
            includeInactiveFilings: pastParameters ? pastParameters.includeInactiveFilings : false
        }
    }
}

const UccAmendmentPartyChange = (data, selectedIndex, jurisdictionState, filingNumber, jurisdictionId, action) => {
    const entity = data[selectedIndex]
    const authorizingParty = data[0]
    return {
        defaultValues: {
            filerReference: '',
            clientReference: '',
            filingNumber: filingNumber || '',
            action: action || '',
            jurisdictionId: jurisdictionId || '',
            affectedParty: {
                organizationName: entity.organizationName || '',
                firstName: entity.firstName || '',
                middleName: entity.middleName || '',
                lastName: entity.lastName || '',
                address: {
                    streetAddress: entity.address.streetAddress || '',
                    city: entity.address.city || '',
                    state: entity.address.state || '',
                    postalCode: entity.address.postalCode || '',
                    country: entity.address.country || ''
                },
                state: {
                    label: '',
                    value: ''
                },
                jurisdictionId: entity.address.state || ''
            },
            authorizingParty: {
                organizationName: authorizingParty.organizationName || '',
                firstName: entity.firstName || '',
                middleName: authorizingParty.middleName || '',
                lastName: authorizingParty.lastName || '',
                address: {
                    streetAddress: authorizingParty.address.streetAddress || '',
                    city: authorizingParty.address.city || '',
                    state: authorizingParty.address.state || '',
                    postalCode: authorizingParty.address.postalCode || '',
                    country: authorizingParty.address.country || ''
                }
            },
            currentParty: {
                organizationName: entity.organizationName || '',
                firstName: entity.firstName || '',
                middleName: entity.middleName || '',
                lastName: entity.lastName || '',
                address: {
                    streetAddress: entity.address.streetAddress || '',
                    city: entity.address.city || '',
                    state: entity.address.state || '',
                    postalCode: entity.address.postalCode || '',
                    country: entity.address.country || ''
                }
            }
        }
    }
}

const uccDirectTerminationForm = (filingToTerminate, applicationId, interactionId, fcsClientReference) => {
    const securedParty = filingToTerminate?.Secureds[0];
    const state = securedParty?.State || '';

    // Determine country from the state value
    const { statesOptions } = require('./constantVariables');
    let detectedCountry = '';
    if (state) {
        if (statesOptions.USA.some(s => s.value === state)) {
            detectedCountry = 'USA';
        } else if (statesOptions.CAN.some(s => s.value === state)) {
            detectedCountry = 'CAN';
        }
    }

    return {
        defaultValues: {
            authorizingParty: {
                organizationName: securedParty?.Name || '',
                firstName: '',
                middleName: '',
                lastName: '',
                taxId: '',
                address: {
                    streetAddress: securedParty?.Address || '',
                    city: securedParty?.City || '',
                    state: state,
                    postalCode: securedParty?.Zip || '',
                    country: detectedCountry
                }
            },
            directAmendment: true,
            applicationId: applicationId,
            filingNumber: filingToTerminate.FilingNumber || '',
            filingDetails: interactionId,
            securedPartyMasking: false,
            jurisdictionId: '',
            jurisdictionCountry: '',
            filerReference: '',
            clientReference: fcsClientReference || ''
        }
    }
}

const uccAttachedFilingColumns = [
    {
        dataField: 'FilingNumber',
        text: 'Filing Number'
    },
    {
        dataField: 'FilingType',
        text: 'Filing Type'
    },
    {
        dataField: 'State',
        text: 'State',
    },
    {
        dataField: 'Status',
        text: 'Status',
        formatter: (cell) => {
            let className = '';
            if (cell === 'Active') {
                className = 'greenLabel';
            } else if (cell === 'Inactive') {
                className = 'redLabel';
            }
            return (
                <span className={className}>
                    {cell}
                </span>
            );
        }
    },
    {
        dataField: 'FilingDate',
        text: 'Date Filed',
        formatter: momentOnlyDateFormatter
    }
]

const EmailLogColumns = [
    {
        dataField: 'id',
        text: 'ID',
        sort: true,
        headerStyle: {
            width: '5rem'
        },
        formatter: (cell) => cell || emptyFormatter()
    },
    {
        dataField: 'from',
        text: 'From',
        sort: true,
        headerStyle: {
            width: '12rem'
        },
        formatter: (cell) => cell || emptyFormatter()
    },
    {
        dataField: 'to',
        text: 'To',
        sort: true,
        headerStyle: {
            width: '12rem'
        },
        formatter: (cell) => cell || emptyFormatter()
    },
    {
        dataField: 'date',
        text: 'Date',
        sort: true,
        formatter: (cell) => cell ? momentDateFormatter(cell) : emptyFormatter(),
        headerStyle: {
            width: '8rem'
        },
    },
    {
        dataField: 'subject',
        text: 'Subject',
        sort: true,
        headerStyle: {
            width: '15rem'
        },
        formatter: (cell) => cell || emptyFormatter()
    },
    {
        dataField: 'fileName',
        text: 'File Name',
        sort: true,
        headerStyle: {
            width: '12rem'
        },
        formatter: (cell) => cell || emptyFormatter()
    },
    {
        dataField: 'status',
        text: 'Status',
        sort: true,
        headerStyle: {
            textAlign: 'center',
            width: '8rem'
        },
        formatter: (cell) => {
            const statusBgColor = {
                    'SUCCESS': 'var(--badge-approved-bg)',
                    'FAILED': '#FAF0F0',
                    'PROCESSING': '#FCF2E6',
                    'SKIPPED': '#F5F7FA',
            }
            const statusColor = {
                'SUCCESS': 'var(--badge-approved-ink)',
                'FAILED': '#BA0D0D',
                'PROCESSING': '#8A4D02',
                'SKIPPED': '#707A8B',
            }

            const bgColor = statusBgColor[cell] || '#6c757d'
            const color = statusColor[cell] || 'white'
            return (
                <div className='table-label' style={{ background: bgColor, color: color }}>
                    {cell || emptyFormatter()}
                </div>
            )
        }
    },
    {
        dataField: 'applicationId',
        text: 'Application ID',
        sort: true,
        headerStyle: {
            width: '12rem'
        },
        formatter: (cell, row) => {
            if (!cell) return emptyFormatter()
            return (
                <Link className={'id-hyper-link'}
                      to={`/applicant/${row.applicationId}`}
                >
                    {cell}
                </Link>
            )
        }
    },
    {
        dataField: 'details',
        text: 'Details',
        sort: false,
        headerStyle: {
            width: '15rem'
        },
        formatter: (cell) => cell || emptyFormatter()
    },
    {
        dataField: 'createdAt',
        text: 'Created At',
        sort: true,
        formatter: momentDateFormatter,
        headerStyle: {
            width: '8rem'
        },
    },
    {
        dataField: 'updatedAt',
        text: 'Updated At',
        sort: true,
        formatter: momentDateFormatter,
        headerStyle: {
            width: '8rem'
        },
    }
]

const EmailLogFilters = [
    {
        id: 'from',
        type: 'text',
        label: 'From Email',
        defaultValue: ''
    },
    {
        id: 'to',
        type: 'text',
        label: 'To Email',
        defaultValue: ''
    },
    {
        id: 'subject',
        type: 'text',
        label: 'Subject',
        defaultValue: ''
    },
    {
        id: 'status',
        type: 'dropdown',
        label: 'Status',
        options: [
            { value: 'SUCCESS', label: 'Success' },
            { value: 'FAILED', label: 'Failed' },
            { value: 'PROCESSING', label: 'Processing' },
            { value: 'SKIPPED', label: 'Skipped' },
        ],
    },
    {
        id: 'applicationId',
        type: 'text',
        label: 'Application ID',
        defaultValue: ''
    }
]

export {
    UccAmendmentPartyChange,
    uccSearchResultColumns,
    uccFilingsTableColumns,
    uccAttachedFilingColumns,
    momentOnlyDateFormatter,
    uccDirectTerminationForm,
    FilingImageColumns,
    UccDocumentRequestColumn,
    DocumentRequestColumn,
    uccSummaryColumns,
    ReportColums,
    FilingDetailsColumns,
    AmendmentDetailsColumns,
    DetailColumn,
    ReceivedDocumentsColumn,
    DebtorsColumn,
    FmcsaColumns,
    ApplicantColumns,
    UccSearchColums,
    uccSearchTableColumns,
    auditColums,
    UccFilingColums,
    DirectAmendmentColums,
    AmendmentHistoryColumns,
    UccSearchData,
    ApplicantFilters,
    ownersObj,
    OwnerDefaultForm,
    DefaultApplicationForm,
    DemoModeApplicationValues,
    DefaultAddress,
    AttachmentsColumn,
    CreditCheckColumn,
    creditReportColumns,
    BranchOfficesColumn,
    mapDataToTable,
    uccFilingColumns,
    infoColumns,
    buisnessBgvColumns,
    bgvSummaryColums,
    continuationColumns,
    clientFilter,
    amendmentsColumn,
    activeColumns,
    monitoringColumns,
    BTOwnersColumn,
    transactionColumn,
    recurrentTransactionColumn,
    transRTDataColumn,
    mapBankTransactionData,
    mapRecurrentBankTransactionData,
    mapAccountSummary,
    sosSearchesColumnsQS,
    sosRecordFilingHistoryColumns,
    mapRegisteredAgentInformation,
    mapBusinessInformation,
    mapSearchResultInfo,
    MAX_RETRIES_FOR_SOS_SEARCH,
    buildUccDefaultFormValues,
    dateFormatter,
    momentDateFormatter,
    EmailLogColumns,
    EmailLogFilters,
    // Old Header======
    BuisnessColumns,
    OwnersColumn,
    // ================
}